/**
 * The banned-word check (QA gate G5, DQ-33).
 *
 * One merged list in six scopes, from packages/config/banned_words.yaml. A scope inherits every
 * word of the scope above it. Matching is word-boundary and case-insensitive, so "advice" is caught
 * and "adviceless" would be too, while "invest" does not fire on "investigation" — the boundary is
 * on the whole word, not a substring.
 *
 * "valuation" is the one word we both ban and legitimately need: the bridge values the business at
 * the sector multiple. It is allowed only inside the fixed strings in this package, and the
 * exception is computed from those strings rather than written down twice.
 */
import banned from "../../config/generated/banned_words.json" with { type: "json" };
import * as fixed from "./provenance.ts";
import { LABEL, PAGE_TITLE, OWNER_TYPE } from "./labels.ts";
import { REASON_TEXT, RISK_TEXT, NEXT_STEP } from "./codes.ts";
import { DEAL_DISCOVERY_STRINGS } from "./deal-discovery.ts";
import { WEB_STRINGS } from "./web.ts";

export type Scope =
  | "client_output"
  | "agent_turn"
  | "marketing"
  /**
   * ADR-016. The Deal Discovery surfaces, where the control is not a word but a phrase: in several
   * regimes *advertising* that you act as an agent is an offence in itself, independent of whether
   * you ever do, so "on behalf of" has to be as unsayable as "guaranteed". The per-market packs
   * exist because Ontario's statute named the activity we must not claim.
   */
  | "deal_discovery"
  | "deal_discovery_au"
  | "deal_discovery_ca";

const RAW = banned as unknown as {
  version: string;
  scopes: Record<string, string[] | { inherits: string; adds: string[] }>;
};

export const BANNED_WORDS_VERSION: string = RAW.version;

function resolve(scope: string): string[] {
  const node = RAW.scopes[scope];
  if (node === undefined) throw new Error(`no banned-word scope ${scope}`);
  if (Array.isArray(node)) return node;
  return [...resolve(node.inherits), ...node.adds];
}

const SCOPES: Record<Scope, string[]> = {
  client_output: resolve("client_output"),
  agent_turn: resolve("agent_turn"),
  marketing: resolve("marketing"),
  deal_discovery: resolve("deal_discovery"),
  deal_discovery_au: resolve("deal_discovery_au"),
  deal_discovery_ca: resolve("deal_discovery_ca"),
};

/**
 * The prohibited-terms pack a jurisdiction names (`jurisdiction.yaml prohibited_terms_pack`) mapped
 * onto a scope. An unknown pack falls back to the strictest shared scope rather than to nothing,
 * because a missing pack must not be a quieter check than a present one.
 */
export function scopeForPack(pack: string): Scope {
  if (pack === "ca") return "deal_discovery_ca";
  if (pack === "au") return "deal_discovery_au";
  return "deal_discovery";
}

export function wordsFor(scope: Scope): string[] {
  return [...SCOPES[scope]];
}

/** A hyphenated entry such as "ai-powered" must survive being escaped into the pattern. */
function pattern(word: string): RegExp {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?<![\\w-])${escaped}(?![\\w-])`, "gi");
}

/** Every fixed string in this package, which is where "valuation" is allowed to appear. */
function approvedStrings(): string[] {
  const out: string[] = [
    fixed.REFUND_LINE, fixed.NOT_ADVICE, fixed.INSUFFICIENT_INPUT,
    ...Object.values(fixed.CONFIDENCE_MARKS),
    ...Object.values(fixed.WATERMARK),
    ...Object.values(fixed.PRODUCT_NAME),
    ...Object.values(LABEL),
    ...Object.values(PAGE_TITLE),
    ...Object.values(OWNER_TYPE),
    ...Object.values(REASON_TEXT),
    ...Object.values(RISK_TEXT),
    ...Object.values(NEXT_STEP),
    // The Deal Discovery fixed strings. A note has to be able to say it is "not advice" and "not a
    // recommendation" — and a model writing prose still must not.
    ...DEAL_DISCOVERY_STRINGS,
    // The public site's disclaimers (WEBSITE_SPEC §6): a negated banned word is a disclaimer, not a claim.
    ...WEB_STRINGS,
  ];
  // Longest first, so blanking "Valuation after, at the same multiple" cannot be pre-empted by a
  // shorter approved string that happens to sit inside it.
  out.sort((a, b) => b.length - a.length);
  return out;
}

export interface BannedHit { word: string; index: number; context: string }

/**
 * Find every banned word in a piece of generated text. Passages that are exactly one of this
 * package's approved strings are skipped, which is how the `valuation` exception is applied
 * without letting a model use the word in prose of its own.
 */
export function findBanned(text: string, scope: Scope): BannedHit[] {
  let searchable = text;
  for (const approved of approvedStrings()) {
    if (approved && searchable.includes(approved)) {
      // Blank the approved string out, keeping the offsets of everything around it.
      searchable = searchable.split(approved).join(" ".repeat(approved.length));
    }
  }
  const hits: BannedHit[] = [];
  for (const word of SCOPES[scope]) {
    for (const m of searchable.matchAll(pattern(word))) {
      hits.push({
        word,
        index: m.index,
        context: text.slice(Math.max(0, m.index - 30), m.index + word.length + 30).trim(),
      });
    }
  }
  return hits.sort((a, b) => a.index - b.index || (a.word < b.word ? -1 : 1));
}

/** G5 in one call: true when the text is clean for that scope. */
export function isClean(text: string, scope: Scope): boolean {
  return findBanned(text, scope).length === 0;
}
